import { CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { Blueprint } from '../../types/blog-generation-agent.types';

interface IBGPostCardProps {
  post: Blueprint;
  onGeneratePost?: (post: Blueprint) => void;
  onViewPost?: (post: Blueprint) => void;
}

/**
 * `BGPostCard` renders a single blog post idea row.
 * Shows the post title, keyword labels, description,
 * and a "Generate Post →" action.
 *
 * @component
 */
function BGPostCard({ post, onGeneratePost, onViewPost }: IBGPostCardProps) {
  const siteSoftDeleted = useSelector(
    (state: RootState) => state?.siteAudit?.isSiteSoftDeleted
  );

  const isGenerateDisabled =
    siteSoftDeleted && post.generation_status !== 'generated';

  return (
    <div className="border border-borderLight mb-4 bg-white shadow-md shadow-light-gray p-5 rounded-lg flex flex-col gap-4">
      {/* Top: Post content */}
      <div>
        <div className="flex items-start gap-3 mb-2">
          {/* Left badge: Search Intent */}
          {post?.angle && (
            <span className="text-xs capitalize font-medium px-2.5 py-1 rounded-2xl bg-[#F5F5F5] text-[#414651] whitespace-nowrap shrink-0 mt-0.5">
              {post?.angle}
            </span>
          )}

          {/* Right content: Title + Keywords */}
          <div className="flex-1 min-w-0">
            <h4 className="text-black text-base font-semibold mb-2">
              {post.title}
            </h4>

            {/* Keyword labels */}
            <div className="flex items-center gap-2 flex-wrap">
              {post.primary_keywords.map((keyword, idx) => (
                <span
                  key={idx}
                  className="text-xs font-semibold px-2.5 py-1 rounded-2xl bg-[#ECFDF3] text-[#1A927B]"
                >
                  {keyword}
                </span>
              ))}
              {post.secondary_keywords?.slice(0, 1).map((keyword, idx) => (
                <span
                  key={idx}
                  className="text-xs font-medium px-2.5 py-1 rounded-2xl bg-[#F5F5F5] text-[#414651]"
                >
                  {keyword}
                </span>
              ))}
              {(post.secondary_keywords?.length || 0) > 1 && (
                <span className="text-xs font-normal px-2.5 py-1 rounded-2xl bg-[#F5F5F5] text-[#414651]">
                  +{post.secondary_keywords.length - 1} more
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-[#414651] text-xs font-medium line-clamp-2">
          {post.synopsis}
        </p>
      </div>

      {/* Bottom: Status indicator + Generate/View Post action */}
      <div className="flex items-center justify-between pt-2">
        {/* Left: Status indicator */}
        <div>
          {post.generation_status === 'generated' ? (
            <div className="flex items-center gap-1.5 text-xs md:text-sm font-semibold text-[#1A927B]">
              <span className="w-2 h-2 rounded-full bg-[#1A927B] inline-block" />
              <span>Generated</span>
            </div>
          ) : null}
        </div>

        {/* Right: Action button */}
        <div>
          {post.generation_status === 'generated' ? (
            <button
              type="button"
              onClick={() => onViewPost?.(post)}
              className="border border-[#D0D5DD] bg-white text-black rounded-lg px-4 py-1 text-sm font-bold cursor-pointer"
            >
              View post
            </button>
          ) : post.generation_status === 'processing' ? (
            <button
              type="button"
              disabled
              className="bg-[#1A927B] text-white rounded-lg px-4 py-1 text-sm font-semibold opacity-70 cursor-not-allowed flex items-center gap-2"
            >
              <span>Generating</span>
              <CircularProgress size={16} color="inherit" />
            </button>
          ) : (
            <button
              type="button"
              disabled={isGenerateDisabled}
              onClick={() => {
                if (!isGenerateDisabled) {
                  onGeneratePost?.(post);
                }
              }}
              className={`bg-[#1A927B] text-white rounded-lg px-4 py-1 text-sm font-semibold transition-colors flex items-center gap-1.5 ${
                isGenerateDisabled
                  ? 'opacity-70 cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
            >
              <span>Generate post</span>
              <span className="text-base leading-none">→</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BGPostCard;
